import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createTriggerProfile } from '@/lib/api/generated/api';
import type { TriggerProfileView } from '@/lib/api/generated/model';

import { toTriggerCreateError } from '../errors';
import { triggerKeys } from '../queryKeys';
import {
  toJiraTriggerProfileCreate,
  toScheduledTriggerProfileCreate,
  toTriggerProfileCreate,
} from '../types';
import type {
  JiraTriggerProfileFormValues,
  ScheduledTriggerProfileFormValues,
  TriggerProfileFormValues,
} from '../types';

/**
 * Create a trigger profile — a Spec 1 `workspace_task_change` profile, a
 * Spec 065 scheduled `github`/`gitlab` profile, or a spec 074 scheduled
 * `jira` profile. The three form shapes are siblings (research.md D10;
 * `types.ts`'s package-level note), not one generic shape, so this hook
 * discriminates on `'project' in values` (Jira's own field) then
 * `'connectionId' in values` (the field only a scheduled shape has) and
 * serializes with the matching `toXCreate` function. Either way the result
 * is a plain `TriggerProfileCreate`, so `createTriggerProfile` itself takes
 * no branch.
 *
 * Mirrors `useCreateWorkspaceSecret` (`features/secrets/hooks/`): calls the
 * generated raw fetcher directly rather than the generated `useCreateTriggerProfile`
 * hook, so the response's discriminated `status` union narrows the success
 * branch instead of being asserted, and every non-201 status maps to a typed
 * refusal the create form can attach to the right field (`errors.ts`).
 *
 * On success, invalidates the list so the new profile appears without a
 * manual cache splice — no optimistic row, for the same reason
 * `useCreateWorkspaceSecret` skips one: this is a deliberate administrative
 * action, not a latency-sensitive interaction.
 */
export function useCreateTriggerProfile(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      values:
        | TriggerProfileFormValues
        | ScheduledTriggerProfileFormValues
        | JiraTriggerProfileFormValues,
    ): Promise<TriggerProfileView> => {
      const body =
        'project' in values
          ? toJiraTriggerProfileCreate(values)
          : 'connectionId' in values
            ? toScheduledTriggerProfileCreate(values)
            : toTriggerProfileCreate(values);
      const response = await createTriggerProfile(workspaceId, body);
      if (response.status === 201) return response.data;
      throw toTriggerCreateError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.all(workspaceId),
      });
    },
  });
}
